import { useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { CheckCircle2, ListChecks, ChevronDown, ChevronUp, CheckCheck } from 'lucide-react'
import { usePartners, useUpdatePartner } from '@/hooks/usePartners'
import { toast } from 'sonner'
import { PARTNER_ONBOARDING_TASKS } from '@/constants/partner'
import ProgressBar from './Progress'
import TaskCard from './TaskCard'
import { Button } from '@/components/ui/button'

function Onboarding({ isWhiteLabel }: { isWhiteLabel: boolean }) {
  const { data: partner } = usePartners()
  const updatePartner = useUpdatePartner()
  const [isExpanded, setIsExpanded] = useState(!partner?.onboarding_completed)
  const progress = partner?.onboarding_progress || {}
  
  const filteredTasks = PARTNER_ONBOARDING_TASKS.filter(task => {
    return task.isWhiteLabel === isWhiteLabel;
  });

  const tasks = filteredTasks.map(task => ({
    ...task,
    completed: progress?.[task.id]?.completed || false,
    completedAt: progress?.[task.id]?.completedAt || null,
  }))

  // Calculate progress
  const completedCount = tasks.filter(t => t.completed).length
  const totalCount = tasks.length
  const progressPercentage = Math.round((completedCount / totalCount) * 100)

  // Toggle task completion
  const toggleTask = async (taskId: string, currentStatus: boolean) => {
    if(partner?.onboarding_completed) return;
    const newProgress = {
      ...progress,
      [taskId]: { completed: !currentStatus, completedAt: !currentStatus ? new Date().toISOString() : null }
    }

    try {
      await updatePartner.mutateAsync({ onboarding_progress: newProgress })
      toast.success(!currentStatus ? 'Task marked as complete!' : 'Task unmarked')
    } catch (error) {
      toast.error('Failed to update task status')
    }
  }
  const handleFinish = async () => {
    await updatePartner.mutateAsync({ onboarding_completed: true })
    toast.success('Onboarding completed!')
    setIsExpanded(false)
  }

  return (
    <Card className="space-y-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="font-bold text-xl flex gap-2 items-center">
              <ListChecks size='20' />  
              Welcome! Let's get you set up
            </h1>
            {!partner?.onboarding_completed && <p className="text-muted-foreground">Follow the instructions to complete this step, and once finished, check the box to mark it as done.</p>}
          </div>
          {partner?.onboarding_completed && <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-4 "
          >
            {isExpanded ? (<ChevronUp />) : (<ChevronDown  />)}
          </Button>}
        </div>
        <ProgressBar 
          completedCount={completedCount} 
          totalCount={totalCount} 
          progressPercentage={progressPercentage} 
        />
      </CardHeader>
      {isExpanded && (
        <CardContent> 
        <div className="space-y-4 ">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} toggleTask={toggleTask} />
          ))}
        </div>
        {progressPercentage === 100 && !partner?.onboarding_completed && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800 mt-10">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">🎉 Congratulations!</h3>
              <p className="text-muted-foreground mb-4">
                You've completed all onboarding tasks. You're ready to start growing your business!
              </p>
              <Button className="w-[150px]" onClick={handleFinish}>
                <CheckCheck className="size-4 " />
                Start using 
              </Button>
            </CardContent>
          </Card>
        )}

        </CardContent>
      )}
    </Card>
  )
}

export default Onboarding 